import { useState, useEffect } from 'react';
import api from '../../../lib/api';
import { normalizeTurkish } from '../../../lib/turkishSearch';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import { Truck, Save, X, Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { calculateTotals, formatCurrency, formatHours } from '../utils/calculations';
import RouteAttachments from './RouteAttachments';

export function RouteFormDialog({
  open,
  onOpenChange,
  formData,
  setFormData,
  editingRoute,
  firms,
  employees,
  vehicles,
  saving,
  onSave,
  onCustomerChange,
  onFetchAllEmployees,
  attachmentsRef,
  customerList,
  t,
}) {
  const [driverComboOpen, setDriverComboOpen] = useState(false);
  const [copilotComboOpen, setCopilotComboOpen] = useState(false);
  const [vehicleComboOpen, setVehicleComboOpen] = useState(false);
  const [manualRit, setManualRit] = useState(false);
  const [showAllDrivers, setShowAllDrivers] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  // Şub 28, 2026 — Subco (alt taşeron) desteği
  const [subcos, setSubcos] = useState([]);        // firma-filtreli
  const [allSubcos, setAllSubcos] = useState([]);  // tüm aktif subco'lar

  // Firma değişince o firmanın subco'larını çek
  useEffect(() => {
    if (!formData.firm_id) { setSubcos([]); return; }
    api.get(`/subcos?firm_id=${formData.firm_id}`)
      .then((r) => setSubcos(r.data?.data?.items || []))
      .catch(() => setSubcos([]));
  }, [formData.firm_id]);

  // cmdk Command filtresi: "özkan" ↔ "ozkan", "şükrü" ↔ "sukru" eşleşsin.
  // value zaten normalize biçimde set ediliyor (aşağıdaki CommandItem'larda),
  // burada sadece kullanıcının yazdığı arama metnini normalize ediyoruz.
  const turkishFilter = (value, search) => {
    if (!search) return 1;
    return normalizeTurkish(value).includes(normalizeTurkish(search)) ? 1 : 0;
  };

  // Dynamic customer config from DB
  const activeCustomer = (customerList || []).find(c => c.slug === formData.customer);
  const customerFields = activeCustomer?.fields || [];
  const calcType = activeCustomer?.calculation_type || 'hours_rate';
  const fieldOptions = activeCustomer?.field_options || {};
  const hasField = (key) => customerFields.includes(key);

  const handleToggleAllDrivers = async () => {
    if (!showAllDrivers && allEmployees.length === 0) {
      setLoadingAll(true);
      try {
        const result = await onFetchAllEmployees();
        setAllEmployees(result || []);
        // Şub 28, 2026 — Tüm subco'ları da çek (firma filtresiz)
        const sr = await api.get('/subcos');
        setAllSubcos(sr.data?.data?.items || []);
      } catch { /* ignore */ }
      setLoadingAll(false);
    }
    setShowAllDrivers(!showAllDrivers);
  };

  const driverList = showAllDrivers ? allEmployees : employees;
  const subcoList = showAllDrivers ? allSubcos : subcos;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-600" />
            {editingRoute ? t('routes.editRoute') : t('routes.addRoute')}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-4">
          {/* Customer Selection - Dynamic from DB */}
          <div className="col-span-2" data-testid="customer-selection">
            <Label className="text-sm font-medium mb-2 block">{t('routes.form.customerRequired')}</Label>
            <div className="flex gap-2 flex-wrap">
              {(customerList || []).map(customer => (
                <Button
                  key={customer.slug}
                  type="button"
                  variant={formData.customer === customer.slug ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => !editingRoute && onCustomerChange(customer.slug)}
                  disabled={editingRoute !== null}
                  data-testid={`customer-btn-${customer.slug}`}
                  className={formData.customer !== customer.slug ? (customer.bg_color || '') : ''}
                  style={formData.customer === customer.slug ? { backgroundColor: customer.color, borderColor: customer.color } : {}}
                >
                  {formData.customer === customer.slug && <Check className="h-3 w-3 mr-1" />}
                  {customer.name}
                </Button>
              ))}
            </div>
            {editingRoute && (
              <p className="text-xs text-slate-500 mt-1">{t('routes.form.customerCannotChange')}</p>
            )}
          </div>

          {/* Firma */}
          {hasField('firm_id') && (
          <div>
            <Label>{t('routes.form.firmRequired')}</Label>
            <Select 
              value={formData.firm_id} 
              onValueChange={(v) => setFormData({...formData, firm_id: v})}
            >
              <SelectTrigger data-testid="firm-select">
                <SelectValue placeholder={t('routes.form.selectFirm')} />
              </SelectTrigger>
              <SelectContent>
                {firms.map(firm => (
                  <SelectItem key={firm.id} value={firm.id}>{firm.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          )}

          {/* Tarih */}
          {hasField('date') && (
          <div>
            <Label>{t('routes.form.dateRequired')}</Label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              data-testid="date-input"
            />
          </div>
          )}

          {/* Route Number */}
          {hasField('route_number') && (
            <div>
              <Label>{t('routes.form.routeNumberLabel')}</Label>
              <Input
                placeholder={t('routes.form.routeNumberPlaceholder')}
                value={formData.route_number}
                onChange={(e) => setFormData({...formData, route_number: e.target.value})}
                data-testid="route-number-input"
              />
            </div>
          )}

          {/* Route Type - dropdown with options or free text */}
          {hasField('route_type') && (
            <div>
              <Label>{t('routes.form.tourTypeRequired')}</Label>
              {fieldOptions.route_type_options?.length > 0 ? (
                <Select 
                  value={formData.route_type} 
                  onValueChange={(v) => setFormData({...formData, route_type: v})}
                >
                  <SelectTrigger data-testid="route-type-select">
                    <SelectValue placeholder={t('routes.form.selectTour')} />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldOptions.route_type_options.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder={t('routes.form.tourTypePlaceholder')}
                  value={formData.route_type}
                  onChange={(e) => setFormData({...formData, route_type: e.target.value})}
                  data-testid="route-type-input"
                />
              )}
            </div>
          )}

          {/* Rit - dropdown with manual entry */}
          {hasField('rit') && (
            <div>
              <Label>{t('routes.form.ritRequired')}</Label>
              {!manualRit ? (
                <Select 
                  value={formData.plaats || ''} 
                  onValueChange={(v) => {
                    if (v === '_manual') {
                      setManualRit(true);
                      setFormData({...formData, plaats: ''});
                    } else {
                      setFormData({...formData, plaats: v});
                    }
                  }}
                >
                  <SelectTrigger data-testid="rit-select">
                    <SelectValue placeholder={t('routes.form.selectRit')} />
                  </SelectTrigger>
                  <SelectContent>
                    {(fieldOptions.rit_options || []).map(rit => (
                      <SelectItem key={rit} value={rit}>{rit}</SelectItem>
                    ))}
                    <SelectItem value="_manual">+ {t('routes.form.manualEntry')}</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex gap-2">
                  <Input
                    placeholder={t('routes.form.enterRit')}
                    value={formData.plaats}
                    onChange={(e) => setFormData({...formData, plaats: e.target.value})}
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setManualRit(false)}
                  >
                    {t('routes.form.list')}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Vehicle Plate - Searchable dropdown + manual input */}
          {hasField('vehicle_plate') && (
          <div>
            <Label>{t('routes.form.vehiclePlateRequired')}</Label>
            <Popover open={vehicleComboOpen} onOpenChange={setVehicleComboOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={vehicleComboOpen}
                  className="w-full justify-between font-normal"
                  disabled={!formData.firm_id}
                  data-testid="vehicle-plate-btn"
                >
                  {formData.license_plate && formData.license_plate !== '_manual'
                    ? formData.license_plate
                    : formData.firm_id ? t('routes.form.searchPlate') : t('routes.form.selectFirmFirst')}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0" align="start">
                <Command filter={turkishFilter}>
                  <CommandInput placeholder={t('routes.form.searchPlatePlaceholder')} />
                  <CommandList>
                    <CommandEmpty>{t('routes.form.vehicleNotFound')}</CommandEmpty>
                    <CommandGroup>
                      {vehicles.map(v => (
                        <CommandItem
                          key={v.id}
                          value={v.license_plate}
                          onSelect={() => {
                            setFormData({...formData, license_plate: v.license_plate});
                            setVehicleComboOpen(false);
                          }}
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${formData.license_plate === v.license_plate ? "opacity-100" : "opacity-0"}`}
                          />
                          <div className="flex flex-col">
                            <span className="font-medium">{v.license_plate}</span>
                            {v.brand && v.model && (
                              <span className="text-xs text-slate-500">{v.brand} {v.model}</span>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t('routes.form.orManualEntry')}</p>
            <Input
              placeholder={t('routes.form.enterPlate')}
              value={formData.license_plate === '_manual' ? '' : formData.license_plate}
              onChange={(e) => setFormData({...formData, license_plate: e.target.value.toUpperCase()})}
              className="mt-1"
              data-testid="vehicle-plate-input"
            />
          </div>
          )}

          {/* Driver */}
          {hasField('driver') && (
          <div className="col-span-2">
            <div className="flex items-center justify-between mb-1">
              <Label>{t('routes.form.driverRequired')}</Label>
              <Button
                type="button"
                variant={showAllDrivers ? "default" : "outline"}
                size="sm"
                className="h-7 text-xs"
                onClick={handleToggleAllDrivers}
                disabled={loadingAll}
                data-testid="toggle-all-drivers-btn"
              >
                {loadingAll ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                {showAllDrivers ? t('routes.form.firmDrivers') || 'Firma Soforleri' : t('routes.form.allDrivers') || 'Tum Soforler'}
              </Button>
            </div>
            <Popover open={driverComboOpen} onOpenChange={setDriverComboOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={driverComboOpen}
                  className="w-full justify-between font-normal"
                  disabled={!formData.firm_id && !showAllDrivers}
                  data-testid="driver-select-btn"
                >
                  {formData.driver_id
                    ? driverList.find(emp => emp.user?.id === formData.driver_id)?.user?.full_name 
                      || employees.find(emp => emp.user?.id === formData.driver_id)?.user?.full_name
                      || formData.driver_name
                      || t('routes.form.selectDriver')
                    : (formData.firm_id || showAllDrivers) ? t('routes.form.searchAndSelectDriver') : t('routes.form.selectFirmFirst')}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                <Command filter={turkishFilter}>
                  <CommandInput placeholder={t('routes.form.searchDriver')} />
                  <CommandList>
                    <CommandEmpty>{t('routes.form.driverNotFound')}</CommandEmpty>
                    <CommandGroup heading={showAllDrivers ? (t('routes.form.allDrivers') || 'Tum Soforler') : undefined}>
                      {driverList.map(emp => {
                        const driverName = emp.user?.full_name || `${emp.profile?.voornaam || ''} ${emp.profile?.naam || ''}`.trim();
                        const firmName = emp.user?.firm_name || firms.find(f => f.id === emp.user?.firm_id)?.name || '';
                        return (
                          <CommandItem
                            key={emp.user?.id}
                            value={`${driverName} ${firmName}`}
                            onSelect={() => {
                              setFormData({
                                ...formData,
                                driver_id: emp.user?.id,
                                driver_name: driverName
                              });
                              setDriverComboOpen(false);
                            }}
                          >
                            <Check
                              className={`mr-2 h-4 w-4 ${formData.driver_id === emp.user?.id ? "opacity-100" : "opacity-0"}`}
                            />
                            <div className="flex flex-col">
                              <span>{driverName}</span>
                              {showAllDrivers && firmName && (
                                <span className="text-xs text-blue-500">{firmName}</span>
                              )}
                              {emp.user?.email && (
                                <span className="text-xs text-slate-500">{emp.user.email}</span>
                              )}
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                    {/* Şub 28, 2026 — Subco (alt taşeron) grubu */}
                    {subcoList.length > 0 && (
                      <CommandGroup heading="Subco (Alt Taşeron)">
                        {subcoList.map((s) => {
                          const firmName = (s.firms && s.firms[0]?.name) || '';
                          const companyName = s.company_name || '';
                          // Mart 01, 2026 — İşletme adı varsa "Barış TRANS — Yasin Heltek" olarak göster
                          const displayLabel = companyName ? `${companyName} — ${s.full_name}` : s.full_name;
                          const driverNameForForm = companyName ? `${companyName} - ${s.full_name}` : s.full_name;
                          return (
                            <CommandItem
                              key={`subco-${s.id}`}
                              value={`${s.full_name} ${companyName} ${firmName} subco`}
                              onSelect={() => {
                                // Subco seçilince: driver bilgisi + sanal plaka otomatik dolar
                                // Plaka manuel düzenlenebilir kalır.
                                const nextData = {
                                  ...formData,
                                  driver_id: s.id,
                                  driver_name: driverNameForForm,
                                };
                                if (s.vehicle_code && !formData.license_plate) {
                                  nextData.license_plate = s.vehicle_code;
                                }
                                setFormData(nextData);
                                setDriverComboOpen(false);
                              }}
                            >
                              <Check className={`mr-2 h-4 w-4 ${formData.driver_id === s.id ? "opacity-100" : "opacity-0"}`} />
                              <div className="flex flex-col">
                                <span className="flex items-center gap-1">
                                  {companyName ? (
                                    <>
                                      <span className="font-semibold">{companyName}</span>
                                      <span className="text-slate-400">—</span>
                                      <span>{s.full_name}</span>
                                    </>
                                  ) : (
                                    s.full_name
                                  )}
                                  <span className="text-[9px] px-1 rounded bg-indigo-100 text-indigo-700">SUBCO</span>
                                </span>
                                {showAllDrivers && firmName && (
                                  <span className="text-xs text-blue-500">{firmName}</span>
                                )}
                                {s.vehicle_code && (
                                  <span className="text-xs text-slate-500">🚐 {s.vehicle_code}</span>
                                )}
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t('routes.form.orManualEntry')}</p>
            <Input
              placeholder={t('routes.form.enterDriverName')}
              value={formData.driver_name}
              onChange={(e) => setFormData({...formData, driver_name: e.target.value})}
              className="mt-1"
              data-testid="driver-name-input"
            />
          </div>
          )}

          {/* Copilot */}
          {hasField('copilot') && (
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-1">
                <Label>{t('routes.form.copilotLabel')}</Label>
                <Button
                  type="button"
                  variant={showAllDrivers ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={handleToggleAllDrivers}
                  disabled={loadingAll}
                  data-testid="toggle-all-copilots-btn"
                >
                  {loadingAll ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  {showAllDrivers ? t('routes.form.firmDrivers') || 'Firma Soforleri' : t('routes.form.allDrivers') || 'Tum Soforler'}
                </Button>
              </div>
              <Popover open={copilotComboOpen} onOpenChange={setCopilotComboOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={copilotComboOpen}
                    className="w-full justify-between font-normal"
                    disabled={!formData.firm_id && !showAllDrivers}
                    data-testid="copilot-select-btn"
                  >
                    {formData.copilot_id
                      ? driverList.find(emp => emp.user?.id === formData.copilot_id)?.user?.full_name 
                        || employees.find(emp => emp.user?.id === formData.copilot_id)?.user?.full_name
                        || formData.copilot_name
                        || t('routes.form.selectCopilot')
                      : (formData.firm_id || showAllDrivers) ? t('routes.form.searchAndSelectCopilot') : t('routes.form.selectFirmFirst')}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command filter={turkishFilter}>
                    <CommandInput placeholder={t('routes.form.searchCopilot')} />
                    <CommandList>
                      <CommandEmpty>{t('routes.form.noEmployeeFound')}</CommandEmpty>
                      <CommandGroup heading={showAllDrivers ? (t('routes.form.allDrivers') || 'Tum Soforler') : undefined}>
                        {driverList.map(emp => {
                          const copilotName = emp.user?.full_name || `${emp.profile?.voornaam || ''} ${emp.profile?.naam || ''}`.trim();
                          const firmName = emp.user?.firm_name || firms.find(f => f.id === emp.user?.firm_id)?.name || '';
                          return (
                            <CommandItem
                              key={emp.user?.id}
                              value={`${copilotName} ${firmName}`}
                              onSelect={() => {
                                setFormData({
                                  ...formData,
                                  copilot_id: emp.user?.id,
                                  copilot_name: copilotName
                                });
                                setCopilotComboOpen(false);
                              }}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${formData.copilot_id === emp.user?.id ? "opacity-100" : "opacity-0"}`}
                              />
                              <div className="flex flex-col">
                                <span>{copilotName}</span>
                                {showAllDrivers && firmName && (
                                  <span className="text-xs text-blue-500">{firmName}</span>
                                )}
                                {emp.user?.email && (
                                  <span className="text-xs text-slate-500">{emp.user.email}</span>
                                )}
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t('routes.form.orManualEntry')}</p>
              <Input
                placeholder={t('routes.form.enterCopilotName')}
                value={formData.copilot_name}
                onChange={(e) => setFormData({...formData, copilot_name: e.target.value, copilot_id: ''})}
                className="mt-1"
                data-testid="copilot-name-input"
              />
            </div>
          )}

          {/* KM Start */}
          {hasField('km_start') && (
            <div>
              <Label>
                {t('routes.form.kmStartLabel')}
                {calcType === 'manual_total' && <span className="text-xs text-slate-400 ml-1">(Opsiyonel)</span>}
              </Label>
              <Input
                type="number"
                placeholder={t('routes.form.kmStartPlaceholder')}
                value={formData.km_start}
                onChange={(e) => setFormData({...formData, km_start: e.target.value})}
                data-testid="km-start-input"
              />
            </div>
          )}

          {/* KM End */}
          {hasField('km_end') && (
            <div>
              <Label>
                {t('routes.form.kmEndLabel')}
                {calcType === 'manual_total' && <span className="text-xs text-slate-400 ml-1">(Opsiyonel)</span>}
              </Label>
              <Input
                type="number"
                placeholder={t('routes.form.kmEndPlaceholder')}
                value={formData.km_end}
                onChange={(e) => setFormData({...formData, km_end: e.target.value})}
                data-testid="km-end-input"
              />
            </div>
          )}

          {/* Stops */}
          {hasField('stops') && (
            <div>
              <Label>Stops</Label>
              <Input
                type="number"
                placeholder="0"
                value={formData.stops}
                onChange={(e) => setFormData({...formData, stops: e.target.value})}
                data-testid="stops-input"
              />
            </div>
          )}

          {/* Stop Price (DHL - maps to start_cost in formData) */}
          {hasField('stop_price') && (
            <div>
              <Label>Stop Prijs</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0"
                value={formData.start_cost}
                onChange={(e) => setFormData({...formData, start_cost: e.target.value})}
                data-testid="stop-price-input"
              />
            </div>
          )}

          {/* Hourly Rate */}
          {hasField('hourly_rate') && (
            <div>
              <Label>{t('routes.form.hourlyRateLabel')}</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.hourly_rate}
                onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                data-testid="hourly-rate-input"
              />
            </div>
          )}

          {/* Start Cost - only when not using stop_price or totaal_excl_btw */}
          {hasField('start_cost') && !hasField('stop_price') && !hasField('totaal_excl_btw') && (
            <div>
              <Label>{t('routes.form.startCostLabel')}</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.start_cost}
                onChange={(e) => setFormData({...formData, start_cost: e.target.value})}
                data-testid="start-cost-input"
              />
            </div>
          )}

          {/* Totaal excl BTW (Bpost - maps to start_cost) */}
          {hasField('totaal_excl_btw') && (
            <div className="col-span-2">
              <Label>Start Kost / Totaal excl BTW</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.start_cost}
                onChange={(e) => setFormData({...formData, start_cost: e.target.value})}
                data-testid="totaal-excl-btw-input"
              />
              <p className="text-xs text-slate-500 mt-1">Totaal excl BTW = Start Kost</p>
            </div>
          )}

          {/* Start Time */}
          {hasField('start_time') && (
          <div>
            <Label>{t('routes.form.startTimeLabel')}</Label>
            <Input
              type="time"
              value={formData.start_time}
              onChange={(e) => setFormData({...formData, start_time: e.target.value})}
              data-testid="start-time-input"
            />
          </div>
          )}

          {/* End Time */}
          {hasField('end_time') && (
          <div>
            <Label>{t('routes.form.endTimeLabel')}</Label>
            <Input
              type="time"
              value={formData.end_time}
              onChange={(e) => setFormData({...formData, end_time: e.target.value})}
              data-testid="end-time-input"
            />
          </div>
          )}

          {/* Break Time */}
          {hasField('break_time') && (
            <div>
              <Label>{t('routes.form.breakTimeLabel')}</Label>
              <Input
                type="time"
                value={formData.break_time}
                onChange={(e) => setFormData({...formData, break_time: e.target.value})}
                data-testid="break-time-input"
              />
            </div>
          )}

          {/* Calculated Values */}
          <div className="col-span-2 bg-slate-50 dark:bg-slate-800 p-4 rounded-lg" data-testid="calculated-values">
            <h4 className="font-medium mb-2">{t('routes.form.calculatedValues')}</h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              {(hasField('km_start') || hasField('km_end')) && (
                <div>
                  <span className="text-slate-500 dark:text-slate-400">{t('routes.form.totalKm')}</span>
                  <span className="font-bold ml-2">
                    {formData.km_end && formData.km_start 
                      ? parseInt(formData.km_end) - parseInt(formData.km_start) 
                      : 0}
                  </span>
                  {calcType === 'stops_price' && <span className="text-xs text-slate-400 ml-1">(bilgi)</span>}
                </div>
              )}
              <div>
                <span className="text-slate-500 dark:text-slate-400">
                  {calcType === 'manual_total' ? 'Aantal Uren' : t('routes.form.workDuration')}
                </span>
                <span className="font-bold ml-2">{formatHours(calculateTotals(formData, calcType).work_hours)}</span>
                {calcType === 'stops_price' && <span className="text-xs text-slate-400 ml-1">(bilgi)</span>}
              </div>
              {calcType === 'stops_price' && (
                <div>
                  <span className="text-slate-500 dark:text-slate-400">Stops x Stop Prijs</span>
                  <span className="font-bold ml-2">{parseInt(formData.stops) || 0} x {formatCurrency(parseFloat(formData.start_cost) || 0)}</span>
                </div>
              )}
              <div>
                <span className="text-slate-500 dark:text-slate-400">
                  {calcType === 'manual_total' ? 'Totaal excl BTW' : t('routes.form.totalAmount')}
                </span>
                <span className="font-bold text-emerald-600 ml-2">{formatCurrency(calculateTotals(formData, calcType).total_cost)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="col-span-2">
            <Label>{t('routes.form.notesLabel')}</Label>
            <Input
              placeholder={t('routes.form.notesPlaceholder')}
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              data-testid="notes-input"
            />
          </div>

          {/* Attachments */}
          <div className="col-span-2 pt-2 border-t">
            <RouteAttachments 
              ref={attachmentsRef}
              routeId={editingRoute?.id} 
              isEditing={!!editingRoute}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-btn">
            <X className="h-4 w-4 mr-2" />
            {t('routes.cancel')}
          </Button>
          <Button onClick={onSave} disabled={saving} data-testid="save-route-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {t('routes.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
